import { z } from 'zod';

export type OrderFormEventType =
  | 'OrderFormLoaded'
  | 'OrderFormStart'
  | 'OrderFormFieldFocus'
  | 'OrderFormFieldBlur'
  | 'OrderFormVoucherUrlLoad'
  | 'OrderFormVoucherEntered';

// Base schema for order form events
export const OrderFormPayloadBaseSchema = z.object({
  cookieConsentVisible: z.boolean(),
  iframeEmbedded: z.boolean().optional(),
  mainProductId: z.number().optional(),
  mainProductTypeId: z.number().optional(),
  defaultPrice: z.number().optional(),
  siteownerId: z.number().optional(),
  pageType: z.enum(['orderform', 'renew']).optional(),
  vendor: z.string().optional(),
  affiliate: z.string().optional(),
  orderformId: z.number().optional(),
  orderCheckoutType: z.enum(['express', 'default']).optional(),
  orderType: z.enum(['initial', 'upsell']).optional(),
  orderBuyerCountry: z.string().optional(),
  orderCurrency: z.string().optional(),
  orderLanguage: z.string().optional(),
  productLanguage: z.string().optional(),
  orderValue: z.number().optional(),
  upsellPosition: z.string().optional(),
  upsellNumber: z.number().optional(),
  paymentMethod: z.string().nullable().optional(),
  paymentProviderId: z.string().nullable().optional(),
  orderBillingType: z.string().optional(),
  referrer: z.string().optional(),
  os: z.string().optional(),
  browser: z.string().optional(),
  browserVersion: z.string().optional(),
  app: z.string().optional(),
  deviceType: z.string().optional(),
  screenWidth: z.number().optional(),
  screenHeight: z.number().optional(),
  hasInitialError: z.boolean().optional(),
  initialError: z.any().optional(),
});

export type OrderFormPayloadBaseType = z.infer<typeof OrderFormPayloadBaseSchema>;

// Order form specific payload schemas
export const OrderFormStartPayloadSchema = OrderFormPayloadBaseSchema.extend({
  firstInput: z.number().optional(),
  largestContentfulPaint: z.number().optional(),
  firstContentfulPaint: z.number().optional(),
});

export const OrderFormFieldFocusPayloadSchema = OrderFormPayloadBaseSchema.extend({
  fieldName: z.string().optional(),
});

export const OrderFormFieldBlurPayloadSchema = OrderFormPayloadBaseSchema.extend({
  fieldName: z.string().optional(),
  hadError: z.boolean().optional(),
  fieldValueProvided: z.boolean().optional(),
});

export const OrderFormVoucherUrlLoadPayloadSchema = OrderFormPayloadBaseSchema.extend({
  voucherCode: z.string(),
  isVoucherValid: z.boolean(),
  errorMessage: z.string().optional(),
});

export const OrderFormVoucherEnteredPayloadSchema = OrderFormPayloadBaseSchema.extend({
  voucherCode: z.string(),
  isVoucherValid: z.boolean(),
  errorMessage: z.string().optional(),
});

export type OrderFormFieldBlurPayload = z.infer<typeof OrderFormFieldBlurPayloadSchema>;
export type OrderFormFieldFocusPayload = z.infer<typeof OrderFormFieldFocusPayloadSchema>;

export type OrderFormEventTypePayloadMap = {
  OrderFormLoaded: z.infer<typeof OrderFormPayloadBaseSchema>;
  OrderFormStart: z.infer<typeof OrderFormStartPayloadSchema>;
  OrderFormFieldFocus: z.infer<typeof OrderFormFieldFocusPayloadSchema>;
  OrderFormFieldBlur: z.infer<typeof OrderFormFieldBlurPayloadSchema>;
  OrderFormVoucherUrlLoad: z.infer<typeof OrderFormVoucherUrlLoadPayloadSchema>;
  OrderFormVoucherEntered: z.infer<typeof OrderFormVoucherEnteredPayloadSchema>;
};

export const OrderFormEventSchemaMap = {
  OrderFormLoaded: OrderFormPayloadBaseSchema,
  OrderFormStart: OrderFormStartPayloadSchema,
  OrderFormFieldFocus: OrderFormFieldFocusPayloadSchema,
  OrderFormFieldBlur: OrderFormFieldBlurPayloadSchema,
  OrderFormVoucherUrlLoad: OrderFormVoucherUrlLoadPayloadSchema,
  OrderFormVoucherEntered: OrderFormVoucherEnteredPayloadSchema,
};
